import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PASSWORD_MAX_LEN, PASSWORD_MIN_LEN } from "@kkp/api-client";
import { Button } from "@kkp/ui";
import { Field } from "../ui/Input";
import { PasswordInput } from "@kkp/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { PasswordRules, isPasswordValid } from "@kkp/ui";
import { changePassword } from "../api/auth";
import { ApiError } from "../api/client";
import { toast } from "@kkp/ui";

const schema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password."),
    newPassword: z
      .string()
      .min(
        PASSWORD_MIN_LEN,
        `Password must be at least ${PASSWORD_MIN_LEN} characters.`,
      )
      .max(
        PASSWORD_MAX_LEN,
        `Password must be at most ${PASSWORD_MAX_LEN} characters.`,
      )
      .refine(isPasswordValid, "Password does not meet all rules."),
    confirm: z.string().min(1, "Please confirm your new password."),
  })
  .refine((d) => d.newPassword === d.confirm, {
    path: ["confirm"],
    message: "Passwords do not match.",
  });

type FormValues = z.infer<typeof schema>;

export type ChangePasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps): React.ReactElement {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirm: "" },
  });

  const newPassword = useWatch({ control, name: "newPassword" });
  const confirm = useWatch({ control, name: "confirm" });

  const confirmFieldError =
    errors.confirm?.message === "Passwords do not match."
      ? undefined
      : errors.confirm?.message;

  const close = React.useCallback(() => {
    onOpenChange(false);
    reset();
  }, [onOpenChange, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password updated. Other devices have been signed out.", {
        title: "Password changed",
      });
      close();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.httpStatus === 401) {
          setError("currentPassword", {
            type: "server",
            message: "Current password is incorrect.",
          });
          return;
        }
        if (err.httpStatus === 400) {
          const message = err.message || "New password is invalid.";
          if (/different from current/i.test(message)) {
            setError("newPassword", {
              type: "server",
              message: "New password must be different from current password.",
            });
          } else {
            setError("newPassword", { type: "server", message });
          }
          return;
        }
      }
      toast.danger("Could not change password. Please try again.", {
        title: "Error",
      });
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Update your account password. You&apos;ll be asked for your current
            password to confirm. Other signed-in devices will be signed out.
          </DialogDescription>
        </DialogHeader>

        <form className="mt-4 grid gap-4" onSubmit={onSubmit}>
          <Field
            label="Current Password"
            required
            error={errors.currentPassword?.message}
          >
            <PasswordInput
              autoComplete="current-password"
              placeholder="Enter current password"
              {...register("currentPassword")}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_minmax(0,260px)] md:gap-6">
            <div className="grid gap-4">
              <Field
                label="New Password"
                required
                error={errors.newPassword?.message}
              >
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  {...register("newPassword")}
                />
              </Field>
              <Field
                label="Confirm New Password"
                required
                error={confirmFieldError}
              >
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  {...register("confirm")}
                />
              </Field>
            </div>
            <PasswordRules
              password={newPassword}
              confirm={confirm}
              showMatch
              heading="Password requirements"
              className="md:mt-7"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
